import { useState } from 'react';
import { useLoaderData } from 'react-router';
import type { SerializeFrom } from '~/types/serialize-from';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import type { loader } from '@modules/professional/page/loaders/overview-page-loader.server';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import type { CompanyImage } from '@modules/professional/type/company-images';
import CompanyPhotoViewer from '@modules/professional/component/about-tab/company-images/CompanyPhotoViewer';
import {
    NumberedAccordion,
    RICH_TEXT_HTML_CLASS,
    TeamGrid,
    stripHtml,
    type NumberedAccordionRow,
} from '~/ui-rocco/pages/detail-shared';

interface PhotoViewerProfessional {
    id: number;
    title: string;
    companyLogoSrc: string;
    logoBackgroundColor: string;
    blockSummary: string;
}

type AboutLoaderData = SerializeFrom<typeof loader>;

const AWARDS_INITIAL = 8;
const AWARDS_STEP = 8;

/**
 * (01) About — about copy on the left, a 2×2 image grid on the right. When the
 * professional has company photos the four tiles are clickable (the fourth
 * carries a "+N more" overlay) and open the shared photo viewer over every
 * image, like prod; otherwise project photos fill the grid statically.
 */
const AboutRowContent = ({
    html,
    images,
    fallbackSrcs,
    professional,
}: {
    html: string;
    images: CompanyImage[];
    fallbackSrcs: string[];
    professional: PhotoViewerProfessional;
}) => {
    const [activeImageId, setActiveImageId] = useState<number | null>(null);
    const hasViewer = images.length > 0;
    // Up to four tiles (Figma 2×2 grid); the fourth carries a "+N more" overlay
    // when there are more, opening the viewer over the full set. A single photo
    // keeps the large editorial tile; the rest scale into the grid.
    const gridImages = images.slice(0, 4);
    const remaining = images.length - gridImages.length;

    return (
        <div className="flex max-md:flex-col max-md:gap-y-6 max-md:pb-4 md:items-start md:justify-between md:pt-[5rem] md:pb-[5rem]">
            <div
                className={`font-sans leading-[1.25] font-medium text-[#181818] max-md:text-base md:max-w-[60.375rem] md:text-[2.0625rem] md:tracking-[-0.0516rem] ${RICH_TEXT_HTML_CLASS}`}
                dangerouslySetInnerHTML={{ __html: html }}
            />
            {gridImages.length === 1 ? (
                <button
                    type="button"
                    onClick={() => setActiveImageId(gridImages[0]!.ID)}
                    aria-label={gridImages[0]!.caption || 'View image'}
                    className="bg-neutral-200 relative aspect-[580/657] shrink-0 overflow-hidden max-md:w-full md:mr-[1rem] md:w-[36.25rem]"
                >
                    <img
                        src={gridImages[0]!.src}
                        alt={gridImages[0]!.caption}
                        loading="lazy"
                        className="absolute inset-0 size-full object-cover transition-transform duration-300 hover:scale-[1.03]"
                    />
                </button>
            ) : gridImages.length > 1 ? (
                <div className="grid shrink-0 grid-cols-2 gap-[0.5rem] max-md:w-full md:mr-[1rem] md:w-[36.25rem]">
                    {gridImages.map((img, index) => {
                        const showMore =
                            index === gridImages.length - 1 && remaining > 0;
                        return (
                            <button
                                key={img.ID}
                                type="button"
                                onClick={() => setActiveImageId(img.ID)}
                                aria-label={
                                    img.caption || `View image ${index + 1}`
                                }
                                className="bg-neutral-200 group relative aspect-square overflow-hidden"
                            >
                                <img
                                    src={img.src}
                                    alt={img.caption}
                                    loading="lazy"
                                    className="absolute inset-0 size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                                />
                                {showMore ? (
                                    <span className="absolute inset-0 flex items-center justify-center bg-black/50 font-sans font-medium text-white max-md:text-lg md:text-[1.5rem]">
                                        +{remaining} more
                                    </span>
                                ) : null}
                            </button>
                        );
                    })}
                </div>
            ) : fallbackSrcs[0] ? (
                <div className="bg-neutral-200 relative aspect-[580/657] shrink-0 overflow-hidden max-md:w-full md:mr-[1rem] md:w-[36.25rem]">
                    <img
                        src={fallbackSrcs[0]}
                        alt=""
                        loading="lazy"
                        className="absolute inset-0 size-full object-cover"
                    />
                </div>
            ) : null}

            {hasViewer && activeImageId !== null ? (
                <CompanyPhotoViewer
                    professional={professional}
                    stack={images}
                    imageId={activeImageId}
                    open
                    toggle={() => setActiveImageId(null)}
                />
            ) : null}
        </div>
    );
};

/** (02) Awards — grey rows (year / organisation / award / project + View Project). */
const AwardsRowContent = ({
    awards,
}: {
    awards: AboutLoaderData['awards'];
}) => {
    const [visible, setVisible] = useState(AWARDS_INITIAL);
    const shown = awards.slice(0, visible);

    return (
        <div className="flex flex-col gap-[0.25rem] max-md:pt-6 max-md:pb-6 md:pt-[5rem] md:pb-[5rem]">
            {shown.map((award) => {
                const project = award.awardedEntities?.edges?.[0]?.node;
                return (
                    <div
                        key={award.id}
                        className="grid items-center gap-x-[1.5rem] gap-y-3 rounded-[0.5rem] bg-[#f6f6f6] max-md:px-5 max-md:py-5 md:min-h-[6.25rem] md:grid-cols-[13%_19%_26%_1fr_auto] md:px-[2.5rem]"
                    >
                        <span className="font-sans leading-tight font-medium text-[#1f1f21] max-md:text-xl md:text-[1.375rem]">
                            {award.year}
                        </span>
                        <AwardField
                            label="Organisation"
                            value={award.institution}
                        />
                        <AwardField label="Award" value={award.title} />
                        <AwardField label="Project" value={project?.Title} />
                        {project?.Link ? (
                            <RoccoLink
                                to={project.Link}
                                className="inline-flex h-10 items-center justify-center rounded-full border-[1.347px] border-[#181818] px-[1.18rem] font-sans text-base font-medium whitespace-nowrap text-[#181818] transition-colors hover:bg-[#181818] hover:text-white max-md:justify-self-start"
                            >
                                View Project
                            </RoccoLink>
                        ) : (
                            <span />
                        )}
                    </div>
                );
            })}
            {visible < awards.length ? (
                <div className="flex justify-center pt-[2.5rem]">
                    <button
                        type="button"
                        onClick={() =>
                            setVisible((value) => value + AWARDS_STEP)
                        }
                        className="inline-flex h-[3.75rem] items-center justify-center rounded-full bg-[#333333] px-[1.75rem] font-sans text-base font-medium text-[#f3f3f3] shadow-[0px_3px_11px_rgba(0,0,0,0.11)] transition-colors hover:bg-[#181818]"
                    >
                        Load More
                    </button>
                </div>
            ) : null}
        </div>
    );
};

const AwardField = ({
    label,
    value,
}: {
    label: string;
    value?: string | null;
}) =>
    value ? (
        <div className="flex min-w-0 flex-col gap-[0.375rem]">
            <span className="font-sans leading-none text-[#767676] max-md:text-xs md:text-[0.875rem] md:tracking-[-0.026rem]">
                {label}
            </span>
            <span className="truncate font-sans leading-none font-medium text-[#141414] max-md:text-sm md:text-[1.25rem] md:tracking-[-0.025rem]">
                {value}
            </span>
        </div>
    ) : (
        <span />
    );

/**
 * About tab body — a numbered accordion matching the Figma section designs:
 * (01) About (copy + company-image grid), (02) Awards (structured rows + Load
 * More), one row per custom library tab, and "Meet the Team" (the portrait
 * grid). Self-hides rows with no content.
 */
export const ProfessionalAboutContentSection = () => {
    const {
        professionalOverview,
        awards,
        customTabs,
        companyImages,
        teamMembers,
        featuredProjects,
    } = useLoaderData<typeof loader>();
    const professional = useProfessionalInitialRouteData();
    const name = professionalOverview.Title;
    const summaryHtml = professionalOverview.BlockSummary;
    const summaryText = summaryHtml ? stripHtml(summaryHtml) : '';

    // Every company photo opens the viewer, but landscape ones lead so the four
    // 4:3 grid tiles read well; the viewer and the "+N more" count still cover
    // all photos. Falls back to project photos (static) when there are none.
    const companyPhotos = [...companyImages].sort(
        (a, b) => Number(a.isPortrait) - Number(b.isPortrait),
    ) as CompanyImage[];
    const projectImageSrcs = (featuredProjects.featuredProjectsList ?? [])
        .map((project) => project.images?.[0]?.src)
        .filter((src): src is string => Boolean(src))
        .slice(0, 3);
    const viewerProfessional: PhotoViewerProfessional = {
        id: professional.ID,
        title: professional.Title,
        companyLogoSrc: professional.CompanyLogo,
        logoBackgroundColor: professional.LogoBackgroundColor,
        blockSummary: summaryHtml ?? '',
    };

    const rows: NumberedAccordionRow[] = [];
    if (summaryHtml) {
        rows.push({
            title: `About ${name}`,
            content: (
                <AboutRowContent
                    html={summaryHtml}
                    images={companyPhotos}
                    fallbackSrcs={projectImageSrcs}
                    professional={viewerProfessional}
                />
            ),
        });
    }
    if (awards.length > 0) {
        rows.push({
            title: 'Awards',
            content: <AwardsRowContent awards={awards} />,
        });
    }
    customTabs.forEach((tab) => {
        if (!tab.content) return;
        rows.push({
            title: tab.title,
            content: (
                <div className="max-md:pb-4 md:pt-[5rem] md:pb-[5rem]">
                    {/* No display heading — the accordion trigger already shows
                        "(0N) {title}", so a heading here just repeats it. */}
                    <div
                        className={`font-sans leading-[1.25] font-medium text-[#181818] max-md:text-base md:text-[2.0625rem] md:tracking-[-0.0516rem] ${RICH_TEXT_HTML_CLASS}`}
                        dangerouslySetInnerHTML={{ __html: tab.content }}
                    />
                </div>
            ),
        });
    });
    if (teamMembers.length > 0) {
        rows.push({
            title: 'Meet the Team',
            content: (
                <div className="max-md:pb-4 md:pt-[5rem] md:pb-[5rem]">
                    <TeamGrid
                        members={teamMembers}
                        className="md:gap-y-[6.25rem]"
                    />
                </div>
            ),
        });
    }

    if (rows.length === 0) return null;

    // On mobile the accordion is the first content element (the intro + summary
    // above are desktop-only), so its leading top border duplicates the sub-nav's
    // bottom border — drop it on mobile, keep it under the desktop summary block.
    const [firstRow] = rows;
    if (firstRow) {
        firstRow.itemClassName = cn(
            firstRow.itemClassName,
            'max-md:border-t-0',
        );
    }

    const defaultOpen = rows[0]?.title;

    return (
        <section className="bg-white">
            {summaryText ? (
                <div className="mx-auto w-full max-w-[120rem] max-md:hidden md:px-[3.125rem] md:pb-[10rem]">
                    <p className="m-0 line-clamp-2 font-sans leading-[1.25] font-medium text-[#181818] max-md:text-base md:max-w-[60.375rem] md:text-[2.0625rem] md:tracking-[-0.0516rem]">
                        {summaryText}
                    </p>
                </div>
            ) : null}

            <NumberedAccordion
                headingLevel={2}
                rows={rows}
                theme="light"
                multiple
                className="md:px-[3.125rem]"
                capClassName="mx-auto w-full max-w-[113.75rem]"
                triggerClassName="md:[&>svg]:size-8"
                defaultOpenTitles={defaultOpen ? [defaultOpen] : undefined}
            />
        </section>
    );
};
